import { BASE_URL } from './constants';
import { getUserSessionCookie } from '../utils/SessionCookie';

export interface GTMEngineerRequest {
  message: string;
  campaignId: string;
  chatSessionId: string | null;
}

/** Session event - sent first */
export interface GTMEngineerSessionEvent {
  type: 'session';
  sessionId: string;
}

/** Step event - tool_call | message | tool_output */
export interface GTMEngineerStepToolCall {
  type: 'step';
  kind: 'tool_call';
  tool: string;
  display_message?: string;
}

export interface GTMEngineerStepMessage {
  type: 'step';
  kind: 'message';
  display_message: string;
}

export interface GTMEngineerStepToolOutput {
  type: 'step';
  kind: 'tool_output';
  tool: string;
  output: string | unknown[] | Record<string, unknown>;
}

export type GTMEngineerStepEvent =
  | GTMEngineerStepToolCall
  | GTMEngineerStepMessage
  | GTMEngineerStepToolOutput;

/** Approval required event - user must approve or reject before change is applied */
export interface GTMEngineerApprovalRequiredEvent {
  type: 'approval_required';
  display_message: string;
  sessionId: string;
}

/** Done event - sent once at end */
export interface GTMEngineerDoneEvent {
  type: 'done';
  actor_type?: string;
  display_message?: string | null;
  done?: true;
  approvalPending?: boolean;
}

export type GTMEngineerStreamEvent =
  | GTMEngineerSessionEvent
  | GTMEngineerStepEvent
  | GTMEngineerApprovalRequiredEvent
  | GTMEngineerDoneEvent;

export interface GTMEngineerApprovalRequiredPayload {
  display_message: string;
  sessionId: string;
}

export const sendGTMEngineerMessageStream = async (
  message: string,
  campaignId: string,
  chatSessionId: string | null,
  onSessionId: (sessionId: string) => void,
  onStep: (step: GTMEngineerStepEvent) => void,
  onApprovalRequired: (payload: GTMEngineerApprovalRequiredPayload) => void,
  onDone: (displayMessage: string | null, approvalPending?: boolean) => void,
  onError: (error: string) => void,
  onComplete: () => void
): Promise<void> => {
  const userSessionToken = getUserSessionCookie();

  const requestBody: GTMEngineerRequest = {
    message,
    campaignId,
    chatSessionId
  };

  try {
    const response = await fetch(`${BASE_URL}/v1/ai-gtm-engineer/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(userSessionToken && { Authorization: `Bearer ${userSessionToken}` })
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(
        `GTM Engineer chat failed with status ${response.status}`
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let approvalPending = false;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (!approvalPending) {
          onComplete();
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim().startsWith('data: ')) continue;

        const jsonStr = line.replace(/^data: /, '').trim();
        if (!jsonStr) continue;

        try {
          const event = JSON.parse(jsonStr) as Record<string, unknown>;

          switch (event.type) {
            case 'session':
              if (event.sessionId) {
                onSessionId(String(event.sessionId));
              }
              break;
            case 'step':
              if (event.kind) {
                onStep(event as unknown as GTMEngineerStepEvent);
              }
              break;
            case 'approval_required':
              onApprovalRequired({
                display_message: String(event.display_message),
                sessionId: String(event.sessionId)
              });
              break;
            case 'done':
              approvalPending = Boolean(event.approvalPending);
              onDone(
                event.display_message != null
                  ? String(event.display_message)
                  : null,
                approvalPending
              );
              if (!approvalPending) {
                onComplete();
                return;
              }
              break;
            default:
              break;
          }
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError);
          onError('Error parsing response data');
          return;
        }
      }

      // Handle complete message in buffer when server doesn't send trailing newline
      if (buffer.trim().startsWith('data: ')) {
        const jsonStr = buffer.replace(/^data: /, '').trim();
        if (jsonStr) {
          try {
            const event = JSON.parse(jsonStr) as Record<string, unknown>;
            buffer = '';

            switch (event.type) {
              case 'session':
                if (event.sessionId) {
                  onSessionId(String(event.sessionId));
                }
                break;
              case 'step':
                if (event.kind) {
                  onStep(event as unknown as GTMEngineerStepEvent);
                }
                break;
              case 'approval_required':
                if (onApprovalRequired && event.display_message != null) {
                  onApprovalRequired({
                    display_message: String(event.display_message),
                    sessionId:
                      event.sessionId != null ? String(event.sessionId) : ''
                  });
                }
                break;
              case 'done':
                approvalPending = Boolean(event.approvalPending);
                onDone(
                  event.display_message != null
                    ? String(event.display_message)
                    : null,
                  approvalPending
                );
                if (!approvalPending) {
                  onComplete();
                  return;
                }
                break;
              default:
                break;
            }
          } catch {
            // Incomplete JSON, keep in buffer for next chunk
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : 'Unknown error occurred');
  }
};

export interface GTMEngineerApprovalRequest {
  chatSessionId: string;
  campaignId: string;
  approved: boolean;
}

/** Stream the continuation after user approves or rejects a proposed action */
export const sendGTMEngineerApprovalStream = async (
  campaignId: string,
  chatSessionId: string,
  approved: boolean,
  onSessionId: (sessionId: string) => void,
  onStep: (step: GTMEngineerStepEvent) => void,
  onApprovalRequired: (payload: GTMEngineerApprovalRequiredPayload) => void,
  onDone: (content: string | null, approvalPending?: boolean) => void,
  onError: (error: string) => void,
  onComplete: () => void
): Promise<void> => {
  const userSessionToken = getUserSessionCookie();

  const requestBody: GTMEngineerApprovalRequest = {
    chatSessionId,
    campaignId,
    approved
  };

  try {
    const response = await fetch(`${BASE_URL}/v1/ai-gtm-engineer/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(userSessionToken && { Authorization: `Bearer ${userSessionToken}` })
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errBody = await response.text();
      throw new Error(
        errBody || `GTM Engineer approval failed with status ${response.status}`
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    let approvalPending = false;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (!approvalPending) {
          onComplete();
        }
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim().startsWith('data: ')) continue;

        const jsonStr = line.replace(/^data: /, '').trim();
        if (!jsonStr) continue;

        try {
          const event = JSON.parse(jsonStr) as Record<string, unknown>;

          switch (event.type) {
            case 'session':
              if (event.sessionId) {
                onSessionId(String(event.sessionId));
              }
              break;
            case 'step':
              if (event.kind) {
                onStep(event as unknown as GTMEngineerStepEvent);
              }
              break;
            case 'approval_required':
              onApprovalRequired({
                display_message: String(event.display_message),
                sessionId:
                  event.sessionId != null ? String(event.sessionId) : ''
              });
              break;
            case 'done':
              approvalPending = Boolean(event.approvalPending);
              onDone(
                event.display_message != null
                  ? String(event.display_message)
                  : null,
                approvalPending
              );
              if (!approvalPending) {
                onComplete();
                return;
              }
              break;
            default:
              break;
          }
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError);
          onError('Error parsing response data');
          return;
        }
      }

      if (buffer.trim().startsWith('data: ')) {
        const jsonStr = buffer.replace(/^data: /, '').trim();
        if (jsonStr) {
          try {
            const event = JSON.parse(jsonStr) as Record<string, unknown>;
            buffer = '';

            switch (event.type) {
              case 'session':
                if (event.sessionId) {
                  onSessionId(String(event.sessionId));
                }
                break;
              case 'step':
                if (event.kind) {
                  onStep(event as unknown as GTMEngineerStepEvent);
                }
                break;
              case 'approval_required':
                onApprovalRequired({
                  display_message: String(event.display_message),
                  sessionId: String(event.sessionId)
                });
                break;
              case 'done':
                approvalPending = Boolean(event.approvalPending);
                onDone(
                  event.display_message != null
                    ? String(event.display_message)
                    : null,
                  approvalPending
                );
                if (!approvalPending) {
                  onComplete();
                  return;
                }
                break;
              default:
                break;
            }
          } catch {
            // Incomplete JSON
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : 'Unknown error occurred');
  }
};

function agentMemoryUrl(campaignId: string): string {
  return `${BASE_URL}/v1/ai-gtm-engineer/memory?campaignId=${encodeURIComponent(campaignId)}`;
}

export async function fetchAgentMemory(campaignId: string): Promise<{
  content: string;
  hadExistingFile: boolean;
}> {
  const userSessionToken = getUserSessionCookie();
  const response = await fetch(agentMemoryUrl(campaignId), {
    headers: {
      ...(userSessionToken && {
        Authorization: `Bearer ${userSessionToken}`
      })
    }
  });

  if (response.status === 404) {
    return { content: '', hadExistingFile: false };
  }

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      text || `Failed to load agent memory (${response.status})`
    );
  }

  const content = await response.text();
  return { content, hadExistingFile: true };
}

export async function uploadAgentMemory(
  campaignId: string,
  content: string
): Promise<void> {
  const userSessionToken = getUserSessionCookie();
  const response = await fetch(agentMemoryUrl(campaignId), {
    method: 'PUT',
    headers: {
      'Content-Type': 'text/markdown; charset=utf-8',
      ...(userSessionToken && {
        Authorization: `Bearer ${userSessionToken}`
      })
    },
    body: content
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      text || `Failed to save agent memory (${response.status})`
    );
  }
}
